/**
 * Thin logging wrapper that adds Sentry breadcrumbs alongside console output.
 * Falls back to plain console if Sentry isn't initialized (test environment).
 *
 * Domain-specific methods (sync, auth, realtime, network) use distinct
 * breadcrumb categories for filtering in the Sentry UI.
 */

import { Sentry } from '../lib/sentry'

function addBreadcrumb(category, level, message, data) {
  try {
    Sentry?.addBreadcrumb?.({ category, level, message, data })
  } catch { /* Sentry not initialized */ }
}

const logger = {
  info(message, data) {
    console.log(message, ...(data ? [data] : []))
    addBreadcrumb('app', 'info', message, data)
  },

  warn(message, data) {
    console.warn(message, ...(data ? [data] : []))
    addBreadcrumb('app', 'warning', message, data)
  },

  error(message, data) {
    console.error(message, ...(data ? [data] : []))
    addBreadcrumb('app', 'error', message, data)
  },

  /** Creates a Sentry issue (captureException). Use for actionable errors. */
  capture(error, context) {
    console.error('[capture]', error, context)
    try {
      Sentry?.captureException?.(error, context ? { extra: context } : undefined)
    } catch { /* Sentry not initialized */ }
  },

  // Domain-specific breadcrumb categories
  sync(message, data) {
    console.log(`[sync] ${message}`, ...(data ? [data] : []))
    addBreadcrumb('sync', 'info', message, data)
  },

  auth(message, data) {
    console.log(`[auth] ${message}`, ...(data ? [data] : []))
    addBreadcrumb('auth', 'info', message, data)
  },

  realtime(message, data) {
    console.log(`[realtime] ${message}`, ...(data ? [data] : []))
    addBreadcrumb('realtime', 'info', message, data)
  },

  network(message, data) {
    console.log(`[network] ${message}`, ...(data ? [data] : []))
    addBreadcrumb('network', 'info', message, data)
  },
}

export default logger
